// @flow

import { push } from "connected-react-router";

import Config from "../constants/Config";
import ActionTypes from "../constants/ActionTypes";
import { getUserNonce } from "../api";
import cartApi from "../api/cart";
import { type AddToCartType, type QuickRegistrationType } from "../types";
import convertToQueryString from "../utils/convertToQueryString";
import GTM from "../utils/gtm";
import { openAlertModal } from "./ui";
import { getProfile } from "./user";

// Get Cart
//
export const getCart =
    (): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.GET_CART_REQUEST,
        });

        return cartApi
            .cartGetState()
            .then((response): any => {
                dispatch({
                    type: ActionTypes.GET_CART_SUCCESS,
                    cart: response,
                });
            })
            .catch((err: any): any => {
                if (err.code === "woocommerce_rest_product_out_of_stock") {
                    dispatch(
                        openAlertModal(
                            "Whoops!",
                            `There’s a problem with an item in your cart and your cart has been cleared. Please try adding the item to your cart again. If you have further problems please contact <a href="mailto:activateuts.website@uts.edu.au">activateuts.website@uts.edu.au</a>`
                        )
                    );
                    dispatch(clearCart());
                } else {
                    dispatch({
                        type: ActionTypes.GET_CART_FAIL,
                        err,
                    });
                    throw err;
                }
            });
    };

// Clear Cart
//
export const clearCart =
    (): any =>
    (dispatch: any, getState: any): any => {
        return cartApi
            .cartClearState()
            .then((response): any => {
                // clear old cart
                dispatch({
                    type: ActionTypes.CLEAR_CART,
                });

                // get new cart
                dispatch(getCart());
            })
            .catch((err: any): any => {
                throw err;
            });
    };

// Add to Cart
//
export const addToCart =
    (addToCartPayload: AddToCartType, isTicketItem?: boolean): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.ADD_TO_CART_REQUEST,
        });

        return cartApi
            .addToCart(addToCartPayload)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.ADD_TO_CART_SUCCESS,
                    cart: response,
                    cartItemUrls: {
                        [addToCartPayload.id]: addToCartPayload.clubDetailUrl,
                    },
                    isTicketItem: isTicketItem || false,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.ADD_TO_CART_FAIL,
                    err,
                });

                throw err;
            });
    };

// Remove from cart
//
export const removeFromCart =
    (cartItemId: string): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.REMOVE_FROM_CART_REQUEST,
        });

        return cartApi
            .removeFromCart(cartItemId)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.REMOVE_FROM_CART_SUCCESS,
                    cart: response,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.REMOVE_FROM_CART_FAIL,
                    err,
                });

                if (err.code === "woocommerce_rest_cart_coupon_error") {
                    dispatch(getCart());

                    dispatch(openAlertModal("Cart Updated", err.message));
                } else {
                    throw err;
                }
            });
    };

// Apply promo/coupon code
//
export const applyPromo =
    (code: string): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.APPLY_PROMO_REQUEST,
        });

        return cartApi
            .applyCoupon(code)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.APPLY_PROMO_SUCCESS,
                    cart: response,
                });

                // log GTM promo code applied event
                GTM.push({
                    event: "promo_applied",
                    promo_code: code,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.APPLY_PROMO_FAIL,
                    err,
                });

                // log GTM promo code denied event
                GTM.push({
                    event: "promo_denied",
                    promo_code: code,
                });

                throw err;
            });
    };

// Remove promo/coupon code
//
export const removePromo =
    (code: string): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.APPLY_PROMO_REQUEST,
        });

        return cartApi
            .removeCoupon(code)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.APPLY_PROMO_SUCCESS,
                    cart: response,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.APPLY_PROMO_FAIL,
                    err,
                });

                throw err;
            });
    };

// Select shipping method (pickup / delivery)
//
export const selectShippingMethod =
    (
        package_id: number = 0,
        rate_id: string,
        updateCheckoutStep?: boolean = false
    ): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.SELECT_SHIPPING_REQUEST,
        });

        return cartApi
            .selectShippingRate(package_id, rate_id)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.SELECT_SHIPPING_SUCCESS,
                    cart: response,
                    shippingStepComplete: updateCheckoutStep,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.SELECT_SHIPPING_FAIL,
                    err,
                });

                throw err;
            });
    };

// Quick Registration
//
export const quickRegistration =
    (quickRegistrationForm: QuickRegistrationType): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.QUICK_REG_REQUEST,
        });
        return cartApi
            .quickRegistration({
                club_id: quickRegistrationForm.clubId,
                email: quickRegistrationForm.email,
            })
            .then((response): any => {
                dispatch({
                    type: ActionTypes.QUICK_REG_SUCCESS,
                    cart: response,
                    clubQuickRegistration: {
                        ...quickRegistrationForm,
                    },
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.QUICK_REG_FAIL,
                    err,
                });

                throw err;
            });
    };

// Set/Update customer billing and shipping cart details
//
export const updateCustomerCartDetails =
    (customerDetails: {}): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.UPDATE_CUSTOMER_CART_REQUEST,
        });

        return cartApi
            .updateCustomer(customerDetails)
            .then((response): any => {
                dispatch({
                    type: ActionTypes.UPDATE_CUSTOMER_CART_SUCCESS,
                    cart: response,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.UPDATE_CUSTOMER_CART_FAIL,
                    err,
                });

                throw err;
            });
    };

// Get checkout payment options
//
export const getPaymentOptions =
    (): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.GET_PAYMENT_OPTIONS_REQUEST,
        });

        return cartApi
            .getPaymentOptions()
            .then((response): any => {
                dispatch({
                    type: ActionTypes.GET_PAYMENT_OPTIONS_SUCCESS,
                    paymentOptions: response,
                });
            })
            .catch((err: any): any => {
                dispatch({
                    type: ActionTypes.GET_PAYMENT_OPTIONS_FAIL,
                    err,
                });

                throw err;
            });
    };

// Reset Checkout Step
//
export const resetCheckOutStep =
    (stepName: string): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.RESET_CHECKOUT_STEP,
            name: stepName,
        });
    };

// Toggle use same billing/shipping detials
//
export const setuseDifferentBillingDetails =
    (useDifferentBillingDetails: string): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.SET_SAME_BILLING_DETAILS,
            useDifferentBillingDetails,
        });
    };

// PayPal - Create Order
//
export const createPaypalOrder =
    (orderId, orderKey): any =>
    (dispatch: any, getState: any): any => {
        const { cart } = getState();
        return getUserNonce().then(nonceResponse => {
            const queryForm = {
                order_comments: "",
                payment_method: "ppcp-gateway",
                "woocommerce-process-checkout-nonce":
                    nonceResponse["woocommerce-process-checkout-nonce"],
                ship_to_different_address: cart.useDifferentBillingDetails
                    ? 1
                    : 0,
            };
            Object.keys(cart.billing_address).forEach(billingField => {
                queryForm[`billing_${billingField}`] =
                    cart.billing_address[billingField];
            });
            Object.keys(cart.shipping_address).forEach(shippingField => {
                queryForm[`shipping_${shippingField}`] =
                    cart.shipping_address[shippingField];
            });

            const requestObject = {
                nonce: nonceResponse["ppc-create-order-nonce"],
                payer: {
                    email_address: cart.billing_address.email,
                    name: {
                        surname: cart.billing_address.last_name,
                        given_name: cart.billing_address.first_name,
                    },
                    address: {
                        country_code:
                            cart.shipping_address.country ||
                            cart.billing_address.country,
                        address_line_1:
                            cart.shipping_address.address_1 ||
                            cart.billing_address.address_1,
                        address_line_2:
                            cart.shipping_address.address_2 ||
                            cart.billing_address.address_2,
                        admin_area_1:
                            cart.shipping_address.state ||
                            cart.billing_address.state,
                        admin_area_2:
                            cart.shipping_address.city ||
                            cart.billing_address.city,
                        postal_code:
                            cart.shipping_address.postcode ||
                            cart.billing_address.postcode,
                    },
                    phone: {
                        phone_type: "HOME",
                        phone_number: {
                            national_number: cart.billing_address.phone,
                        },
                    },
                },
                bn_code: "Woo_PPCP",
                // context: "checkout",
                context: "pay-now",
                order_id: orderId,
                order_key: orderKey,
                form: convertToQueryString(queryForm, true),
            };

            return cartApi.paypalCreateOrder(requestObject);
        });
    };

// Checkout order
//
export const checkoutOrder =
    (paymentMethod?: string): any =>
    (dispatch: any, getState: any): any => {
        const { cart } = getState();
        return cartApi.checkoutOrder({
            billing_address: cart.billing_address,
            shipping_address: cart.shipping_address,
            payment_method: paymentMethod || "eway",
            customer_note: "",
        });
    };

// Checkout success
//
export const checkoutSuccess =
    (orderSuccessObject: {}, redirectUrlOveride?: string): any =>
    (dispatch: any, getState: any): any => {
        const { clubQuickRegistration, ticketItems } = getState().cart;

        const ticketToken = orderSuccessObject?.ticket_token;

        dispatch(getProfile(true, true));

        if (redirectUrlOveride) {
            // a custom redirect url override was passed, redirect to it
            dispatch(push(redirectUrlOveride));
        } else if (clubQuickRegistration.clubUrl) {
            // is club quick registration success
            // redirect to club quick registration success page
            dispatch(push(`${clubQuickRegistration.clubUrl}&checkout=success`));
        } else if (ticketToken) {
            dispatch(
                push(
                    `${Config.routes.checkoutSuccess}/${orderSuccessObject.order_id}?ticket_token=${ticketToken}`
                )
            );
        } else if (ticketItems.length > 0) {
            // redirect to checkout success page with tickets link
            dispatch(
                push(
                    `${Config.routes.checkoutSuccess}/${orderSuccessObject.order_id}?t=1`
                )
            );
        } else {
            // redirect to checkout success page
            dispatch(
                push(
                    `${Config.routes.checkoutSuccess}/${orderSuccessObject.order_id}`
                )
            );
        }

        // reset/clear cart
        dispatch(clearCart());
    };
